import { Icon } from '@iconify/react/dist/iconify.js'
import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { getAllNotification } from '../helper/Api/AdminApi'
import SendNotificationModal from './NotificationAdd'

const customStyles = `
.question-row {
  padding: 12px 0px;
}
.question-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
}
.option-item {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.option-label {
  font-size: 12.5px;
  color: #374151;
  line-height: 1.3;
}
.checked-option {
  font-weight: 600;
  color: #111827;
}
.custom-radio {
  margin: 0;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}
.modal-content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  z-index: 1001;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}
`

const NotificationLayer = () => {
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [notifications, setNotifications] = useState([])

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 25, 50, 'All'];
  const [searchTerm, setSearchTerm] = useState('');

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await getAllNotification();
      if (response.Status === 1) {
        const sortedNotifications = response.NotifcationList.sort(
          (a, b) => new Date(b.CreatedOn) - new Date(a.CreatedOn)
        );
        setNotifications(sortedNotifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications()
  }, [])

  const handleSuccess = () => {
    fetchNotifications();
  }

  const getNotificationForText = (notification) => {
    if (notification.IsSingleUser) return 'Single User'
    if (notification.IsAllUser) return 'All Users'
    return 'Filtered Users'
  }

  const filteredNotifications = notifications.filter(notification => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (notification.Title?.toLowerCase() || '').includes(searchLower) ||
      (notification.Description?.toLowerCase() || '').includes(searchLower)
    );
  });

  const totalItems = filteredNotifications.length;
  const totalPages = Math.ceil(totalItems / (pageSize === 'All' ? totalItems : pageSize));
  
  const startIndex = pageSize === 'All' ? 0 : (currentPage - 1) * pageSize;
  const endIndex = pageSize === 'All' ? totalItems : currentPage * pageSize;
  
  const paginatedNotifications = pageSize === 'All' 
    ? filteredNotifications 
    : filteredNotifications.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let start = currentPage - halfVisible;
      let end = currentPage + halfVisible;
      
      if (start < 1) {
        start = 1;
        end = maxVisiblePages;
      }
      
      if (end > totalPages) {
        end = totalPages;
        start = totalPages - maxVisiblePages + 1;
      }
      
      for (let i = start; i <= end; i++) pages.push(i);
    }
    
    return pages;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (pageSize === 'All') {
      setCurrentPage(1);
    }
  }, [pageSize]);


  return (
    <>
      <style>{customStyles}</style>
      <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          {/* Show dropdown */}
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm text-secondary-light">Show:</span>
            <div className="dropdown">
              <button 
                className="btn btn-outline-secondary btn-sm dropdown-toggle py-2 px-3 radius-6"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={loading}
              >
                {pageSize === 'All' ? 'All' : pageSize}
              </button>
              <ul className="dropdown-menu shadow-sm border-0 radius-8 py-2">
                {pageSizeOptions.map((option) => (
                  <li key={option}>
                    <button
                      className={`dropdown-item py-2 px-3 text-sm ${pageSize === option ? 'active bg-primary-50 text-primary-600' : ''}`}
                      onClick={() => handlePageSizeChange(option)}
                      disabled={loading}
                    >
                      {option}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Search input */}
          <form className="navbar-search">
            <input
              type="text"
              className="form-control radius-8 h-40-px w-auto"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={loading}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </form>
        </div>

        <button
          className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          onClick={() => setShowModal(true)}
          disabled={loading}
        >
          <Icon icon="ic:baseline-plus" className="icon text-lg line-height-1" />
          Send New Notification
        </button>
      </div>
        <div className="card-body p-40">
          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <>
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col" className="text-center">Action</th>
                    <th scope="col">Title</th>
                    <th scope="col">Message</th>
                    <th scope="col">Sent On Date</th>
                    <th scope="col">Notification For</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedNotifications.length > 0 ? (
                    paginatedNotifications.map((notification) => (
                      <tr key={notification.ID}>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center">
                            <button
                              type="button"
                              className="bg-primary-50 text-primary-600 bg-hover-primary-100 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => {
                                // Handle edit if needed
                              }}
                              disabled={loading}
                            >
                              <Icon icon="lucide:edit" className="icon text-lg" />
                            </button>
                          </div>
                        </td>
                        <td>{notification.Title}</td>
                        <td style={{ maxWidth: '300px' }}>
                          {notification.Description}
                        </td>
                        <td>
                          {new Date(notification.CreatedOn).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td>{getNotificationForText(notification)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        No notifications found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <div className="d-flex align-items-center gap-3">
              <span className="text-secondary-light text-sm">
                Showing {pageSize === 'All' ? 1 : Math.min(startIndex + 1, totalItems)} to{" "}
                {Math.min(endIndex, totalItems)} of {totalItems} entries
              </span>
            </div>

            {pageSize !== 'All' && totalPages > 1 && (
              <ul className="pagination d-flex flex-wrap align-items-center gap-2 m-0 p-0">
                <li className="page-item">
                  <button
                    className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    style={{ opacity: currentPage === 1 ? 0.4 : 1 }}
                  >
                    <Icon icon="ep:d-arrow-left" className="text-lg" />
                  </button>
                </li>

                {getPageNumbers().map((page) => (
                  <li className="page-item" key={page}>
                    <button
                      className={`page-link radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px fw-medium text-sm transition-all ${
                        currentPage === page
                          ? "bg-primary-600 text-white shadow-sm"
                          : "bg-neutral-100 text-secondary-light hover:bg-neutral-200"
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}

                <li className="page-item">
                  <button
                    className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    style={{ opacity: currentPage === totalPages ? 0.4 : 1 }}
                  >
                    <Icon icon="ep:d-arrow-right" className="text-lg" />
                  </button>
                </li>
              </ul>
            )}
          </div>
          </>
          )}
        </div>
        {/* Use the modal component */}
        <SendNotificationModal
          showModal={showModal}
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
          loading={loading}
        />
      </div>
    </>
  )
}

export default NotificationLayer